import { checkUser } from "api/ssr/user";
import type { NextPageContext } from "next";
import Navbar from "../components/Navbar";
import BudgetWidget from "../components/BudgetWidget";
import ClassicCard from "../components/ClassicCard";
import Tables from "../components/Tables";
import RadioBox from "../components/RadioBox";
import { PaperDownload } from "react-iconly";
import { Calendar } from "react-iconly";
import { ChevronDown } from "react-iconly";
import referralUsers from "api/referral/users";
import React from "react";
import { useApp } from "components/app";
import PulseLoader from "react-spinners/PulseLoader";
import Head from "next/head";

const ReferralPage: React.FC<any> = (_ctx: NextPageContext) => {
  const [userInfo, setUserInfo] = React.useState<any>(null);
  const [updateTables, setUpdateTables] = React.useState<number>(0);
  const [loader, setLoader] = React.useState<boolean>(true);
  const { notify } = useApp();

  React.useEffect(() => {
    checkUser();
    getUserInfo();
  }, []);

  React.useEffect(() => {
    if (userInfo) {
      setLoader(false);
    } else {
      setLoader(true);
    }
  }, [userInfo]);

  const getUserInfo = async () => {
    const userInfo = await referralUsers.getReferralUserData();
    setUserInfo(userInfo);
  };

  const steps = [
    {
      title: "Step 1 - Share your unique referral link",
      description:
        "People who click on it will be attributed to your link and you’ll be able to track how many sign up in this dashboard.",
      icon: "/images/step1.svg",
    },
    {
      title: "Step 2 - People join Blindspot using your link and get a gift",
      description:
        "Users you refer will receive $25 worth of credits, so that’s just another reason to recommend people to use your link!",
      icon: "/images/step2.svg",
    },
    {
      title: "Step 3 - Get paid",
      description:
        "If a user you recommended to us creates their first successful campaign worth at least $500, you’ll receive $100 and you’ll be able to track all your rewards here. You can cash out using the “Cash Out” button in this dashboard.",
      icon: "images/step3.svg",
    },
  ];

  const budgetWidgets = [
    {
      loader: loader,
      otherClass: "w-3/12 mr-7 tablet:w-full tablet:mb-7",
      title: "Total earnings",
      value: userInfo ? userInfo.alltime : 0,
      dinero: true,
      after: "",
      icon: "Wallet",
    },
    {
      loader: loader,
      otherClass: "w-3/12 mr-7 tablet:w-full tablet:mb-7",
      title: "Total referrals",
      value: userInfo ? userInfo.referee_count.count : 0,
      dinero: false,
      after: "",
      icon: "People",
    },
    {
      loader: loader,
      otherClass: "w-6/12 tablet:w-full tablet:mb-7",
      title: "Current balance",
      value: userInfo ? userInfo.balance : 0,
      label: "available",
      value2: userInfo ? userInfo.hold : 0,
      label2: "on hold",
      dinero: true,
      after: "",
      icon: "Chart",
      withdraw: true,
    },
  ];

  const onCopyToClipboard = () => {
    const selBox = document.createElement("textarea");
    selBox.style.position = "fixed";
    selBox.style.left = "0";
    selBox.style.top = "0";
    selBox.style.opacity = "0";
    selBox.value = `https://portal.seeblindspot.com/auth/register?refId=${userInfo.referrer_code}`;
    document.body.appendChild(selBox);
    selBox.focus();
    selBox.select();
    document.execCommand("copy");
    document.body.removeChild(selBox);
    notify("The link has been copied to clipboard!", "success");
  };

  return (
    <div>
      <Head>
        <title>
          {userInfo ? "Referral | Blindspot" : "Log In - Blindspot Referrals"}
        </title>
      </Head>
      <Navbar title="Referral Program" logout={true} />
      <div className="flex space-y-7 flex-col px-7 pt-7 pb-20 tablet:px-4">
        <div className="bg-dark-200 rounded-20 w-full flex flex-col p-5">
          <div className="flex flex-row tablet:flex-col pb-7 border-b-2 border-dark-300 border-opacity-20 justify-between">
            <div className="flex flex-col w-1/2 tablet:w-full">
              <h1 className="font-semibold mb-4 text-xl">
                Spread the word, get the dough
              </h1>
              <h1 className="text-dark-300">
                Use your personalized link to send users to the Blindspot
                Platform. This link is unique to you and will track the number
                of users you refer to Blindspot and create a campaign.
              </h1>
            </div>
            <div className="flex flex-row w-5/12 tablet:w-full items-center tablet:mt-7">
              <div className="bg-grey-default overflow-x-auto bg-opacity-20 rounded-20 rounded-r-none p-4 w-10/12 tablet:w-10/12 relative">
                {!loader && (
                  <h1 className="whitespace-nowrap">
                    https://portal.seeblindspot.com/auth/register?refId=
                    {userInfo ? userInfo.referrer_code : ""}
                  </h1>
                )}
                {loader && (
                  <h1 className="text-center">
                    <PulseLoader color={"#eeeff0"} size={8} loading={true} />
                  </h1>
                )}
              </div>
              <div
                className="bg-primarydark-default w-2/12 rounded-20 rounded-l-none p-4 flex justify-center items-center cursor-pointer hover:shadow-primaryOutlineHover focus:shadow-primaryOutlineHover border-2 border-primarydark-default"
                onClick={onCopyToClipboard}
              >
                <div className="">
                  <PaperDownload size={18} set="light" />
                </div>
                <h1 className="ml-2 tablet:hidden text-sm font-semibold">
                  Copy
                </h1>
              </div>
            </div>
          </div>
          <div className="flex flex-row tablet:flex-col w-full pt-7 tablet:space-y-7">
            {steps.map((step, key: number) => {
              return (
                <div className="flex flex-row w-4/12 tablet:w-full" key={key}>
                  <div className="referral-icon-container flex-shrink-0 rounded-full border border-dark-300 border-opacity-40 mr-4 tablet:ml-0">
                    <img className="m-auto p-3" src={step.icon} />
                  </div>
                  <div className="flex flex-col mr-4">
                    <h1 className="font-semibold mb-2 text-lg">{step.title}</h1>
                    <h1 className="text-dark-300">{step.description}</h1>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
        <div className="flex  flex-row tablet:flex-col pb-7 tablet:pb-0 border-b-2 border-dark-300 border-opacity-20 ">
          {budgetWidgets.map((widget, key: number) => {
            return (
              <BudgetWidget
                key={key}
                otherClass={widget.otherClass}
                title={widget.title}
                value={widget.value}
                value2={widget.value2}
                label={widget.label}
                label2={widget.label2}
                dinero={widget.dinero}
                after=""
                icon={widget.icon}
                withdraw={widget.withdraw}
                loader={loader}
                onSucessfulWithdrawRequest={() => {
                  getUserInfo();
                  setUpdateTables(+1);
                }}
              />
            );
          })}
        </div>
        <Tables count={updateTables}></Tables>
      </div>
    </div>
  );
};

export default ReferralPage;
